import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ClientSessionService } from '@brighthub/uisession';
import { CopilotContextComposerService } from '@brighthub/copilot';
import {
  FilterChipGroupComponent,
  FilterChipOption,
} from '../shared/filter-chip-group/filter-chip-group.component';
import { ClientJourneyStateService } from './client-journey-state.service';
import { JourneyEventDetailDialogComponent } from './journey-event-detail-dialog/journey-event-detail-dialog.component';
import {
  ClientJourneyState,
  JourneyEvent,
  JourneyQuickFilter,
  JourneySeverity,
  JourneySourceType,
} from './client-journey.model';

const CHANNEL_ICONS: Record<string, string> = {
  email:  'pi pi-envelope',
  sms:    'pi pi-comment',
  phone:  'pi pi-phone',
  chat:   'pi pi-comments',
  system: 'pi pi-cog',
  crm:    'pi pi-briefcase',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  email:                    'Email',
  sms:                      'SMS',
  call:                     'Call',
  chat:                     'Chat',
  opportunity_created:      'Opportunity Created',
  opportunity_updated:      'Opportunity Updated',
  opportunity_committed:    'Opportunity Committed',
  opportunity_completed:    'Opportunity Completed',
  opportunity_withdrawn:    'Opportunity Withdrawn',
  action_created:           'Action Created',
  action_updated:           'Action Updated',
  action_completed:         'Action Completed',
  action_blocked:           'Action Blocked',
  action_withdrawn:         'Action Withdrawn',
  outcome_detected:         'Outcome Detected',
  escalation:               'Escalation',
  handoff:                  'Handoff',
  financial_milestone:      'Financial Milestone',
  system:                   'System Event',
};

const COMMUNICATION_TYPES = new Set(['email', 'sms', 'call', 'chat']);
const OPPORTUNITY_TYPES = new Set([
  'opportunity_created', 'opportunity_updated', 'opportunity_committed',
  'opportunity_completed', 'opportunity_withdrawn',
]);
const ACTION_TYPES = new Set([
  'action_created', 'action_updated', 'action_completed',
  'action_blocked', 'action_withdrawn',
]);

const QUICK_FILTER_LABELS: Record<JourneyQuickFilter, string> = {
  'all':                    'All',
  'communications':         'Communications',
  'ai-handled':             'AI-Handled',
  'escalations':            'Escalations',
  'opportunity-lifecycle':  'Opportunities',
  'action-lifecycle':       'Actions',
};

const SOURCE_TYPE_LABELS: Record<JourneySourceType | 'all', string> = {
  all:      'All Sources',
  fa:       'FA',
  client:   'Client',
  aiAgent:  'AI Agent',
  system:   'System',
};

const QUICK_FILTER_OPTIONS: FilterChipOption[] = (
  Object.entries(QUICK_FILTER_LABELS) as [JourneyQuickFilter, string][]
).map(([id, label]) => ({
  id,
  label,
  ariaLabel: `Filter: ${label}`,
}));

@Component({
  selector: 'bai-client-journey',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FilterChipGroupComponent, FormsModule, SkeletonModule, TagModule],
  providers: [DialogService],
  templateUrl: './client-journey.component.html',
  styleUrl: './client-journey.component.scss',
})
export class ClientJourneyComponent {
  private readonly _session = inject(ClientSessionService);
  private readonly _stateService = inject(ClientJourneyStateService);
  private readonly _dialogService = inject(DialogService);
  private readonly _copilotComposer = inject(CopilotContextComposerService);
  private _dialogRef: DynamicDialogRef | null = null;

  readonly loading = signal(true);

  private readonly _sessionId = computed(
    () => this._session.selectedSession()?.uiSessionId ?? null
  );

  constructor() {
    effect(() => {
      const id = this._sessionId();
      if (id) {
        this._stateService.ensureState(id);
        this.loading.set(false);
      } else {
        this.loading.set(false);
      }
    });
  }

  readonly state = computed((): ClientJourneyState | null => {
    const id = this._sessionId();
    if (!id) return null;
    return this._stateService.storeSnapshot()[id] ?? null;
  });

  // ---------------------------------------------------------------------------
  // Filter / sort / search signals
  // ---------------------------------------------------------------------------

  readonly searchQuery = signal('');
  readonly activeFilter = signal<JourneyQuickFilter>('all');
  readonly sourceFilter = signal<JourneySourceType | 'all'>('all');
  readonly sortDir = signal<'desc' | 'asc'>('desc');

  // ---------------------------------------------------------------------------
  // Derived: filtered + sorted events
  // ---------------------------------------------------------------------------

  readonly filteredEvents = computed((): JourneyEvent[] => {
    const s = this.state();
    if (!s) return [];

    const q = this.searchQuery().toLowerCase().trim();
    const filter = this.activeFilter();
    const source = this.sourceFilter();
    const dir = this.sortDir();

    let items = [...s.events];

    // Quick filter
    if (filter === 'communications') {
      items = items.filter(e => COMMUNICATION_TYPES.has(e.eventType));
    } else if (filter === 'ai-handled') {
      items = items.filter(e => e.sourceType === 'aiAgent');
    } else if (filter === 'escalations') {
      items = items.filter(e => e.eventType === 'escalation');
    } else if (filter === 'opportunity-lifecycle') {
      items = items.filter(e => OPPORTUNITY_TYPES.has(e.eventType));
    } else if (filter === 'action-lifecycle') {
      items = items.filter(e => ACTION_TYPES.has(e.eventType));
    }

    // Source filter
    if (source !== 'all') {
      items = items.filter(e => e.sourceType === source);
    }

    // Keyword search
    if (q) {
      items = items.filter(e =>
        e.title.toLowerCase().includes(q) ||
        e.summary.toLowerCase().includes(q) ||
        e.tags.some(t => t.toLowerCase().includes(q)) ||
        e.sourceName.toLowerCase().includes(q)
      );
    }

    // Sort
    items.sort((a, b) => {
      const cmp = a.occurredAt.localeCompare(b.occurredAt);
      return dir === 'desc' ? -cmp : cmp;
    });

    return items;
  });

  // ---------------------------------------------------------------------------
  // Expose constants to template
  // ---------------------------------------------------------------------------

  readonly quickFilters = Object.keys(QUICK_FILTER_LABELS) as JourneyQuickFilter[];
  readonly quickFilterOptions = QUICK_FILTER_OPTIONS;
  readonly sourceTypes = Object.keys(SOURCE_TYPE_LABELS) as (JourneySourceType | 'all')[];

  quickFilterLabel(f: JourneyQuickFilter): string {
    return QUICK_FILTER_LABELS[f];
  }

  sourceTypeLabel(s: JourneySourceType | 'all'): string {
    return SOURCE_TYPE_LABELS[s];
  }

  onQuickFilterSelection(selection: string | string[]): void {
    if (Array.isArray(selection)) return;
    if (
      this.quickFilters.includes(selection as JourneyQuickFilter)
    ) {
      this.activeFilter.set(selection as JourneyQuickFilter);
    }
  }

  // ---------------------------------------------------------------------------
  // Formatting helpers
  // ---------------------------------------------------------------------------

  formatDate(iso: string): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(iso));
  }

  formatTime(iso: string): string {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(iso));
  }

  relativeTime(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  }

  eventTypeLabel(eventType: string): string {
    return EVENT_TYPE_LABELS[eventType] ?? eventType;
  }

  channelIcon(channel: string): string {
    return CHANNEL_ICONS[channel] ?? 'pi pi-circle';
  }

  // ---------------------------------------------------------------------------
  // Visual helpers
  // ---------------------------------------------------------------------------

  dotColorClass(eventType: string): string {
    if (COMMUNICATION_TYPES.has(eventType)) return 'bai-dot--communication';
    if (OPPORTUNITY_TYPES.has(eventType))   return 'bai-dot--opportunity';
    if (ACTION_TYPES.has(eventType))        return 'bai-dot--action';
    if (eventType === 'outcome_detected')   return 'bai-dot--outcome';
    if (eventType === 'escalation')         return 'bai-dot--escalation';
    if (eventType === 'financial_milestone') return 'bai-dot--milestone';
    if (eventType === 'handoff')            return 'bai-dot--handoff';
    return 'bai-dot--system';
  }

  cardAccentClass(eventType: string): string {
    if (COMMUNICATION_TYPES.has(eventType)) return 'bai-card--communication';
    if (OPPORTUNITY_TYPES.has(eventType))   return 'bai-card--opportunity';
    if (ACTION_TYPES.has(eventType))        return 'bai-card--action';
    if (eventType === 'outcome_detected')   return 'bai-card--outcome';
    if (eventType === 'escalation')         return 'bai-card--escalation';
    if (eventType === 'financial_milestone') return 'bai-card--milestone';
    if (eventType === 'handoff')            return 'bai-card--handoff';
    return 'bai-card--system';
  }

  severityTagSeverity(severity: JourneySeverity): string {
    const map: Record<JourneySeverity, string> = {
      info:    'info',
      warn:    'warn',
      danger:  'danger',
      success: 'success',
    };
    return map[severity];
  }

  severityLabel(severity: JourneySeverity): string {
    const map: Record<JourneySeverity, string> = {
      info:    'Info',
      warn:    'Warning',
      danger:  'Critical',
      success: 'Success',
    };
    return map[severity];
  }

  sourceTypeIcon(sourceType: string): string {
    const map: Record<string, string> = {
      fa:      'pi pi-user',
      client:  'pi pi-id-card',
      aiAgent: 'pi pi-bolt',
      system:  'pi pi-cog',
    };
    return map[sourceType] ?? 'pi pi-circle';
  }

  // ---------------------------------------------------------------------------
  // Row interaction
  // ---------------------------------------------------------------------------

  openEventDetail(event: JourneyEvent): void {
    this._dialogRef?.close();
    this._dialogRef = this._dialogService.open(JourneyEventDetailDialogComponent, {
      header: event.title,
      data: { event },
      width: '560px',
      modal: true,
      dismissableMask: true,
      closeOnEscape: true,
    });
  }

  addEventToCopilot(event: JourneyEvent): void {
    const preview = this._truncate(event.title, 20);
    this._copilotComposer.addContext({
      sourceType: 'journey-event',
      sourceId: event.id,
      label: `Journey: ${preview}`,
      fullText:
        `[Journey | ${event.id}] ` +
        `Type: ${this.eventTypeLabel(event.eventType)}; ` +
        `Occurred: ${event.occurredAt}; ` +
        `Source: ${this.sourceTypeLabel(event.sourceType)} (${event.sourceName}); ` +
        `Channel: ${event.channel}; ` +
        `Title: ${event.title}; ` +
        `Summary: ${event.summary}; ` +
        `Tags: ${event.tags.length > 0 ? event.tags.join(', ') : '—'}; ` +
        `Opportunities: ${event.linkedOpportunityIds.length}; ` +
        `Actions: ${event.linkedActionIds.length}; ` +
        `Outcomes: ${event.linkedOutcomeIds.length}`,
    });
  }

  // ---------------------------------------------------------------------------
  // Track-by helpers
  // ---------------------------------------------------------------------------

  trackEvent(_: number, e: JourneyEvent): string { return e.id; }
  trackFilter(_: number, f: JourneyQuickFilter): string { return f; }
  trackSource(_: number, s: JourneySourceType | 'all'): string { return s; }

  private _truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }
}
